import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  businessAddressSchema,
  type BusinessAddressFormValues,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_BUSINESS,
} from '~domains/partner-application/shared/constants';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { AddressSuggestions } from '@breezeventures/web-ph-address-suggestions/address-suggestions';
import { zodResolver } from '@hookform/resolvers/zod';

const suggestionFieldNames = {
  country: 'country',
  region: 'region',
  province: 'province',
  cityOrMunicipality: 'cityOrMunicipality',
  postalCode: 'postalCode',
  barangay: 'barangay',
};

type BusinessAddressProps = {
  initialValues?: BusinessAddressFormValues;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: BusinessAddressFormValues) => Promise<void>;
};

export const BusinessAddress = ({
  initialValues,
  onBack,
  onClose,
  onSubmit,
}: BusinessAddressProps) => {
  const methods = useForm<BusinessAddressFormValues>({
    resolver: zodResolver(businessAddressSchema),
    mode: 'all',
    defaultValues: initialValues ?? {
      country: 'PHL',
      region: '',
      province: '',
      cityOrMunicipality: '',
      postalCode: '',
      barangay: '',
      streetAndHouse: '',
      unitAndFloor: '',
    },
  });

  useEffect(() => {
    if (initialValues) {
      methods.trigger();
    }
  }, [initialValues, methods]);

  const handleSubmit = async (values: BusinessAddressFormValues) => {
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_BUSINESS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <Typography.Title size="s" className="py-6">
            What&#39;s your registered business address?
          </Typography.Title>

          <div className="flex flex-col gap-3">
            <AddressSuggestions fieldNames={suggestionFieldNames} />

            <Controller
              name="streetAndHouse"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="House number, Street name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="unitAndFloor"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="Unit/Apartment (if any)"
                  labelPosition="inner"
                />
              )}
            />
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
